/** In-house course completion. None of these records is a PTW licence or role grant. */
export const TRAINING_COURSE_ID = 'badreor-ptw-inhouse'
export const TRAINING_COURSE_VERSION = '1.0-draft'

export type TrainingAssessorRole = 'QHSE' | 'OPERATIONS_TEAM_LEAD'
export type TrainingRubricAnswer = { met: boolean; evidence: string }
export type TrainingRubricAnswers = Record<string, TrainingRubricAnswer>
export type TrainingAssessmentAnswers = Record<string, string>

export type TrainingCourseSummary = {
  id: string
  version: string
  title: string
  status: 'draft' | 'released' | 'withdrawn'
  required_module_ids: string[]
  pass_percent: number
  critical_question_ids: string[]
  rubric_ids: string[]
  question_count: number
}

export type TrainingQuestion = {
  id: string
  prompt: string
  options: { id: string; text: string }[]
  critical: boolean
}

export type TrainingAssessmentFeedback = { question_id: string; correct: boolean; feedback: string }
export type TrainingAssessmentAttempt = {
  id: string
  score_percent: number
  correct_count: number
  total_questions: number
  critical_correct: boolean
  passed: boolean
  submitted_at: string
  feedback: TrainingAssessmentFeedback[]
}

export type TrainingAssessorSite = {
  designation_id: string
  site_id: string
  site_name: string
  assessor_role: TrainingAssessorRole
}

export type TrainingPracticalRecord = {
  id: string
  site_id: string
  site_name: string
  assessor: { person_id: string; display_name: string; role: TrainingAssessorRole }
  result: 'passed' | 'needs_repeat'
  checks: TrainingRubricAnswers
  permit_reference: string
  observed_at: string
  recorded_at: string
  declaration: boolean
  feedback: string
}

export type TrainingCertificate = {
  id: string
  certificate_number: string
  learner_person_id: string
  learner_name: string
  course_id: string
  course_version: string
  course_title: string
  issued_at: string
  assessment_attempt_id: string
  practical_observation_id: string
  assessor_name: string
  assessor_role: TrainingAssessorRole
  synthetic: boolean
  /** Present on records issued after 0024; old immutable certificates remain unchanged. */
  staging?: boolean
  draft: boolean
  statement: string
  content_digest: string
}

export type TrainingStatus = {
  course: TrainingCourseSummary
  learner: { person_id: string; display_name: string }
  completed_module_ids: string[]
  assessment: { attempt_count: number; passed: boolean; latest_attempt: TrainingAssessmentAttempt | null }
  practical: TrainingPracticalRecord | null
  certificate: TrainingCertificate | null
  can_issue_certificate: boolean
  blockers: string[]
  /** Current caller's explicit designations, never inferred from an operational role. */
  assessor_sites: TrainingAssessorSite[]
  /** Public question text only. Correct answers stay behind the RPC boundary. */
  assessment_questions: TrainingQuestion[]
}

export type TrainingAssessorQueue = {
  course: TrainingCourseSummary
  generated_at: string
  assessor_sites: TrainingAssessorSite[]
  learners: {
    site_id: string
    site_name: string
    person_id: string
    display_name: string
    completed_module_count: number
    required_module_count: number
    assessment_passed: boolean
    practical_result: 'passed' | 'needs_repeat' | null
    certificate_id: string | null
    status: TrainingStatus
  }[]
}
